import { useEffect, useRef, useState } from 'preact/hooks';
import { route } from 'preact-router';
import { BASE_PATH } from '../../shared/types';
import { api, ApiError } from '../lib/api';
import { forgetMe } from '../lib/auth';
import { reportingFetch } from '../lib/clientErrors';

interface Summary { email: string; events: number; googleConnections: number }

export function DeleteAccount({ email }: { email: string }) {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const confirmation = useRef<HTMLInputElement>(null);
  const deleteButton = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open) {
      confirmation.current?.focus({ preventScroll: true });
      // Reveal the confirmation inside the popover without moving the dashboard.
      const popover = confirmation.current?.closest<HTMLElement>('.account-popover');
      if (popover) popover.scrollTop = popover.scrollHeight;
    } else if (wasOpen.current) deleteButton.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open]);

  const start = async () => {
    setOpen(true); setError(null);
    try { setSummary(await api.get<Summary>('/account')); } catch { setSummary({ email, events: 0, googleConnections: 0 }); }
  };

  const del = async () => {
    setBusy(true); setError(null);
    try {
      await reportingFetch(`${BASE_PATH}/api/account`, {
        method: 'DELETE', credentials: 'same-origin', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ confirmEmail: confirm }),
      }).then(async (r) => { if (!r.ok) throw new ApiError(r.status, ((await r.json().catch(() => ({}))) as { error?: string }).error ?? 'error'); });
      forgetMe();
      route(`${BASE_PATH}/?deleted=1`, true);
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'confirmation_mismatch' ? 'The email address does not match this account.' : 'Could not delete the account. Please try again.');
    } finally { setBusy(false); }
  };

  const matches = confirm.trim().toLowerCase() === email.toLowerCase();

  return (
    <section class="card stack account">
      <h2>Account</h2>
      <p class="small">Signed in as <strong>{email}</strong>.</p>
      {!open ? (
        <div><button class="danger-outline" ref={deleteButton} onClick={start}>Delete account…</button></div>
      ) : (
        <div class="stack">
          <p class="small"><strong>This permanently deletes your account.</strong></p>
          <ul class="small">
            <li>Your {summary ? `${summary.events} event${summary.events === 1 ? '' : 's'}` : 'events'} will be deleted, and their registration links will stop working.</li>
            <li>{summary && summary.googleConnections > 0 ? `${summary.googleConnections} Google connection${summary.googleConnections === 1 ? '' : 's'} will be revoked.` : 'Any Google connections will be revoked.'}</li>
            <li>Spreadsheets and signed permission forms already in your Google Drive are <strong>not</strong> deleted. They belong to you; remove them in Drive if you no longer need them.</li>
            <li>You can sign in again later with the same email, but you will start with no events.</li>
          </ul>
          <label class="small"><span>Type <strong>{email}</strong> to confirm</span>
            <input type="email" ref={confirmation} autoComplete="off" value={confirm} onInput={(e) => setConfirm((e.target as HTMLInputElement).value)} />
          </label>
          {error && <p class="alert">{error}</p>}
          <div class="row">
            <button class="danger-solid" disabled={!matches || busy} onClick={del}>{busy ? 'Deleting…' : 'Permanently delete my account'}</button>
            <button class="secondary" onClick={() => { setOpen(false); setConfirm(''); setError(null); }}>Cancel</button>
          </div>
        </div>
      )}
    </section>
  );
}
